import { useState } from 'react'
import type { Block, BlockCreateInput } from '@/types/blocks'
import { remapSelection } from '@/features/editor/editorSlice'
import { lessonsApi } from '@/features/editor/lessonsApi'
import { useAppDispatch } from '@/store/hooks'
import { useCreateBlockMutation } from '@/features/editor/blocksApi'
import { useSaveStatusWrap } from '@/features/editor/hooks/useSaveStatusWrap'
import { useVideoUploads } from '@/features/editor/components/shared/VideoUploadContext'
import { rememberRenderKey } from '@/features/editor/lib/blockRenderKeys'

interface Input {
  lessonId: number
  courseId: number
  onBlockCreated: (blockId: number) => void
}

interface CreateAt {
  body: BlockCreateInput
  /** Что показать в ленте до ответа сервера (без id — его выдаст хук). */
  placeholder: Block
  /** Что выделить, если сервер отказал. */
  fallbackId: number | null
  errMsg: string
}

/**
 * Мгновенное создание экрана: временная запись (отрицательный id — нажимать на
 * неё нельзя, панель неактивна) сразу видна и выделена, ответ сервера подменяет
 * её на том же месте. Сервер кладёт блок в конец урока — туда же встаёт и
 * временная запись; переноса после создания нет.
 */
export const useOptimisticBlockCreate = ({ lessonId, courseId, onBlockCreated }: Input) => {
  const dispatch = useAppDispatch()
  const wrap = useSaveStatusWrap()
  const videoUploads = useVideoUploads()
  const [createBlock, { isLoading }] = useCreateBlockMutation()
  // Лента одна на все уроки: без урока создания заглушка «создаётся» висела бы и в том уроке,
  // на который автор переключился, пока экран создавался (zaer5).
  const [creatingIn, setCreatingIn] = useState<number | null>(null)

  const createAt = ({ body, placeholder, fallbackId, errMsg }: CreateAt) => {
    setCreatingIn(lessonId)
    const tempId = -Date.now()
    const patch = dispatch(
      lessonsApi.util.updateQueryData('getLessonBlocks', lessonId, (draft) => {
        draft.push({ ...placeholder, id: tempId })
      }),
    )
    onBlockCreated(tempId)
    return wrap(
      createBlock({ lessonId, courseId, body, placedByCaller: true })
        .unwrap()
        .then((created) => {
          rememberRenderKey(created.id, tempId)
          // Файл могли бросить на холст, пока блок создавался: загрузка держит временный
          // id и без алиаса теряет блок («блока уже нет»), а полоса прогресса пропадает.
          dispatch(remapSelection({ lessons: [], blocks: [[tempId, created.id]] }))
          dispatch(
            lessonsApi.util.updateQueryData('getLessonBlocks', lessonId, (draft) => {
              const i = draft.findIndex((b) => b.id === tempId)
              const rest = draft.filter((b) => b.id !== created.id && b.id !== tempId)
              rest.splice(i === -1 ? rest.length : i, 0, created)
              return rest
            }),
          )
          onBlockCreated(created.id)
        })
        .catch((err: unknown) => {
          patch.undo()
          // Файл могли бросить на временный блок: блока не будет — загрузка идёт в никуда.
          videoUploads.abortBlock(tempId)
          if (fallbackId !== null) onBlockCreated(fallbackId)
          throw err
        })
        .finally(() => setCreatingIn(null)),
      errMsg,
    )
  }

  return { createAt, isCreating: isLoading && creatingIn === lessonId }
}
